import axios from 'axios';
import { authService } from './authService';

interface UploadResponse {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
  message?: string;
}

export interface StorageItem {
  key: string;
  url: string;
}

class StorageService {
  private baseUrl: string;

  constructor() {
    // Usa a variável de ambiente VITE_API_URL definida para o backend
    this.baseUrl = `${import.meta.env.VITE_API_URL}/api/storage`;
  }

  /**
   * Faz upload de um arquivo para o S3
   * @param file O arquivo a ser enviado
   * @param folder Pasta opcional dentro do bucket (ex: 'products', 'users')
   * @param filename Nome do arquivo opcional (se não fornecido, usa o nome original do arquivo)
   * @param oldKey Chave S3 de arquivo antigo para ser excluído (opcional)
   * @returns Promise com a resposta contendo URL e chave do arquivo
   */
  async uploadFile(
    file: File, 
    folder: string = 'general', 
    filename?: string,
    oldKey?: string
  ): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      
      if (filename) {
        formData.append('filename', filename);
      }
      
      if (oldKey) {
        formData.append('oldKey', oldKey);
      }
      
      const response = await axios.post(`${this.baseUrl}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      return {
        success: true,
        url: response.data.url,
        key: response.data.key
      };
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo:', error);
      return {
        success: false,
        error: 'Falha ao fazer upload do arquivo. Tente novamente.'
      };
    }
  }

  /**
   * Exclui um arquivo do S3
   * @param key Chave S3 do arquivo a ser excluído
   * @returns Promise com a resposta da operação
   */
  async deleteFile(key: string): Promise<UploadResponse> {
    try {
      const response = await axios.delete(`${this.baseUrl}/delete`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        data: {
          key
        }
      });
      
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido ao excluir arquivo'
      };
    }
  }

  /**
   * Substitui um arquivo no S3 (exclui o antigo e faz upload do novo)
   * @param file Novo arquivo
   * @param oldKey Chave do arquivo antigo a ser substituído
   * @param folder Pasta opcional
   * @param filename Nome do arquivo opcional
   * @returns Promise com resposta do upload
   */
  async replaceFile(
    file: File,
    oldKey: string,
    folder: string = 'general',
    filename?: string
  ): Promise<UploadResponse> {
    return this.uploadFile(file, folder, filename, oldKey);
  }

  /**
   * Gera um nome de arquivo único baseado no timestamp e nome original
   * @param originalFilename Nome original do arquivo
   * @returns Nome único para o arquivo
   */
  generateUniqueFilename(originalFilename: string): string {
    const timestamp = new Date().getTime();
    const extension = originalFilename.split('.').pop() || '';
    const cleanName = originalFilename
      .replace(/\.[^/.]+$/, "") // Remove a extensão
      .replace(/[^a-zA-Z0-9]/g, "-") // Substitui caracteres não alfanuméricos por hífen
      .toLowerCase();
    
    return `${cleanName}-${timestamp}.${extension}`;
  }
}

export const storageService = new StorageService();
